'use client';

import { Children } from 'react';

import { Role, type RoleProps } from '@ariakit/react';
import cx from 'classnames';

import { type TrackEventProps, trackEvent } from '~/ui/components/analytics';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { invariant } from '~/utils/invariant';

import styles from './button.module.scss';
import { ButtonSize, ButtonVariant } from './types';

export interface ButtonProps extends RoleProps<'button'> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  /**
   * Renders a white button with black text instead of a black button with white text
   *
   * Commonly used on desktop spotlights.
   */
  invert?: boolean;

  event?: TrackEventProps;
}

export function Button({
  size = ButtonSize.REGULAR,
  variant = ButtonVariant.PRIMARY,
  invert = false,
  event,
  className,
  onClick,
  ...props
}: ButtonProps) {
  // This is buggy with the async button
  invariant(
    size === ButtonSize.ICON ? Children.count(props.children) === 1 : true,
    'multiple children provided to icon button',
  );

  return (
    <Role.button
      {...props}
      className={cx(styles.button, typographyStyles.button, className, {
        [styles.invert]: invert,
        [styles.primary]: variant === ButtonVariant.PRIMARY,
        [styles.primaryTransparent]: variant === ButtonVariant.PRIMARY_TRANSPARENT,
        [styles.secondary]: variant === ButtonVariant.SECONDARY,
        [styles.secondaryTransparent]: variant === ButtonVariant.SECONDARY_TRANSPARENT,
        [styles.theme]: variant === ButtonVariant.THEME,
        [styles.large]: size === ButtonSize.REGULAR,
        [styles.small]: size === ButtonSize.TINY,
        [styles.icon]: size === ButtonSize.ICON,
      })}
      onClick={e => {
        if (event) {
          trackEvent(event);
        }
        onClick?.(e);
      }}
    />
  );
}
